<script setup lang="ts">
import { ref, reactive, onMounted, watch, onUnmounted, nextTick, shallowRef, computed, defineAsyncComponent } from 'vue';

import { useScroll } from "@/composables/useScroll";
import { useRoute } from '#app';
import { useRenderLoop } from '@tresjs/core';
import { useUnitScale } from '@/composables/useUnitScale';
import { usePerformanceMode } from '@/composables/usePerformanceMode';

import StarsParticles from '@/components/tresJs/StarsParticles.vue';
import IlluminatedCircle from '@/components/tresJs/IlluminatedCircle.vue';
const FloatingRocks = defineAsyncComponent(() => import('@/components/tresJs/FloatingRock.vue'));
const TextSection = defineAsyncComponent(() => import('@/components/tresJs/contents/TextSection.vue'));
const Cards = defineAsyncComponent(() => import('@/components/tresJs/contents/Cards.vue'));
const ImageTextSection = defineAsyncComponent(() => import('@/components/tresJs/contents/ImageTextSection.vue'));
const BookingGenerating = defineAsyncComponent(() => import('@/components/tresJs/contents/BookingGenerating.vue'));
const sceneDiagnosticsComponent = process.dev
  ? defineAsyncComponent(() => import('@/components/tresJs/SceneDiagnostics.vue'))
  : null;

const route = useRoute();
const isMobile = ref(false);
const canvasRef = ref(null);
const circleRef = ref(null);
const isFooterCircleActive = ref(false);
const isSceneVisible = ref(true);
const isReducedVisuals = ref(false);

const statsComponent = process.dev
  ? defineAsyncComponent(async () => {
      const module = await import('@tresjs/cientos');
      return module.StatsGl;
    })
  : null;

const threeComponents = shallowRef({
  'TexteSection': TextSection,
  'Cards': Cards,
  'ImageTextSection': ImageTextSection,
  'BookingGenerating': BookingGenerating,
});
const cameraRef = ref(null);
const cameraVisionRatio = reactive({
  width: 0,
  height: 0,
});

const props = defineProps({
  pageContent: {
    type: Object,
    default: () => ({}),
  },
});

const footerCircle = ref(null);

const { scrollY } = useScroll();
const { onLoop, pause, resume } = useRenderLoop();
const { isLowPower } = usePerformanceMode();
let unsubscribeLoop;
let sceneObserver: IntersectionObserver | null = null;

const updateCameraVision = () => {
  if (cameraRef.value) {
    const { getVisibleSize } = useUnitScale(cameraRef.value);
    const { width, height } = getVisibleSize();
    cameraVisionRatio.width = width;
    cameraVisionRatio.height = height;
  }
};

const updateFooterCircle = () => {
  if (!cameraRef.value) return;

  const { getVisibleSize } = useUnitScale(cameraRef.value);
  const { unitsPerPixelY } = getVisibleSize();

  if (typeof window !== 'undefined' && window.innerWidth < 1024) {
    const footerCircleScale = window.innerWidth * 0.75 * unitsPerPixelY;
    footerCircle.value = {
      scale: footerCircleScale,
      position: [0, footerCircleScale * 0.5, 0],
    };
    return;
  }

  footerCircle.value = {
    scale: (isLowPower.value || isReducedVisuals.value) ? 1 : 1.25,
    position: [0, 0.7, 0],
  };
};

const resize = () => {
  if (typeof window !== 'undefined') {
    isMobile.value = window.innerWidth < 1024;
  }
  updateCameraVision();
  updateFooterCircle();
  circleRef.value?.updateCirclePosition();
};

const getCanvasElement = () => {
  const target = canvasRef.value as any;
  if (!target) return null;
  if (target.$el instanceof HTMLElement) return target.$el as HTMLElement;
  if (target instanceof HTMLElement) return target;
  return null;
};

const handleSceneVisibility: IntersectionObserverCallback = (entries) => {
  const entry = entries[0];
  const visible = !!entry?.isIntersecting;
  isSceneVisible.value = visible;
  if (!visible) {
    pause();
  } else if (!isMobile.value && typeof document !== 'undefined' && document.visibilityState === 'visible' && !isReducedVisuals.value) {
    resume();
  }
};

const handleVisibilityChange = () => {
  if (typeof document === 'undefined') return;
  if (document.visibilityState === 'hidden') {
    pause();
  } else if (isSceneVisible.value && !isMobile.value && !isReducedVisuals.value) {
    resume();
  }
};

const handleActivity = () => {
  isReducedVisuals.value = false;
};

const starFieldCount = computed(() => ((isLowPower.value || isReducedVisuals.value) ? 20000 : 80000));
const starFieldSize = computed(() => ((isLowPower.value || isReducedVisuals.value) ? 55 : 75));
const starFieldVolume = computed(() => ((isLowPower.value || isReducedVisuals.value) ? { x: 6, y: 42, z: 3 } : { x: 8, y: 50, z: 4 }));
const starFieldOpacity = computed(() => ((isLowPower.value || isReducedVisuals.value) ? 0.35 : 0.5));
const starFieldSpeed = computed(() => ((isLowPower.value || isReducedVisuals.value) ? 0.6 : 1));

const shouldRenderCanvas = computed(() => !isMobile.value);
const showFloatingRocks = computed(() => route.name === 'Contact' && !isLowPower.value && !isReducedVisuals.value);
const showDebugStats = computed(() => Boolean(statsComponent) && route.hash.includes('#debug'));
const showDiagnostics = computed(() => Boolean(sceneDiagnosticsComponent));

onMounted(() => {
  nextTick(() => {
    if (typeof window !== 'undefined') {
      isMobile.value = window.innerWidth < 1024;
      window.addEventListener('resize', resize, { passive: true });
      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('pointermove', handleActivity, { passive: true });
      window.addEventListener('keydown', handleActivity);
      window.addEventListener('wheel', handleActivity, { passive: true });
    }

    updateCameraVision();
    updateFooterCircle();

    const target = getCanvasElement();
    if (!sceneObserver && target) {
      sceneObserver = new IntersectionObserver(handleSceneVisibility, { threshold: 0.15 });
      sceneObserver.observe(target);
    }

    setTimeout(() => {
      circleRef.value?.updateCirclePosition();
      isFooterCircleActive.value = true;
    }, 1000);

  });
});

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', resize);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('pointermove', handleActivity);
    window.removeEventListener('keydown', handleActivity);
    window.removeEventListener('wheel', handleActivity);
  }
  if (sceneObserver) {
    sceneObserver.disconnect();
    sceneObserver = null;
  }
  if (unsubscribeLoop) unsubscribeLoop();
});



watch(() => props.pageContent, (newValue, oldValue) => {
  isFooterCircleActive.value = false;
  if (cameraRef.value) {
    cameraRef.value.position.y = 0;
  }
  nextTick(() => {
    isFooterCircleActive.value = true;
  })
  setTimeout(() => {
      circleRef.value?.updateCirclePosition();
      isFooterCircleActive.value = true;
    }, 1000);
}, { deep: true });

unsubscribeLoop = onLoop(() => {
  if (cameraRef.value && scrollY.value && cameraVisionRatio.height) {
    cameraRef.value.position.y = -scrollY.value / window.innerHeight * cameraVisionRatio.height;
  }
});

watch(isLowPower, () => {
  updateFooterCircle();
});

watch(isMobile, (value) => {
  if (value) {
    pause();
    if (sceneObserver) {
      sceneObserver.disconnect();
    }
  } else if (isSceneVisible.value && typeof document !== 'undefined' && document.visibilityState === 'visible') {
    nextTick(() => {
      const target = getCanvasElement();
      if (target) {
        if (!sceneObserver) {
          sceneObserver = new IntersectionObserver(handleSceneVisibility, { threshold: 0.15 });
        } else {
          sceneObserver.disconnect();
        }
        sceneObserver.observe(target);
      }
      if (!isReducedVisuals.value) {
        resume();
      }
    });
  }
});

watch(isReducedVisuals, (value) => {
  updateFooterCircle();
  if (!value && isSceneVisible.value && !isMobile.value && typeof document !== 'undefined' && document.visibilityState === 'visible') {
    resume();
  }
});

watch(scrollY, () => {
  handleActivity();
});

</script>

<template>
   
  <TresCanvas 
  v-if="shouldRenderCanvas"
  ref="canvasRef"
  id="main-canvas" 
  shadows
      preset="realistic" 
      :clear-color="`#000000`"
  window-size
  >
  <component v-if="showDebugStats" :is="statsComponent" />
  <component v-if="showDiagnostics" :is="sceneDiagnosticsComponent" />

<TresPerspectiveCamera 
      ref="cameraRef"
      fov="35" 
      :position="[0, 0, 5]" />
    
    <Suspense>
      <FloatingRocks 
        v-if="showFloatingRocks"  
        :camera="cameraRef"
        linkedObject=".contact-form" 
        varient="1" 
        :scale="1" 
        :position="[1.65, -.25, 0]" />

    </Suspense>
    <TresSpotLight 
            v-if="route.name === 'Contact'"  
            ref="lightRef"
            linkedObject=".contact-form" 
            :position="[-5.7, 5, -6]"
            :angle="0.45"
            :penumbra="1"
            :decay=".8"
            :intensity="20"
            :color="'#b4ade5'"
            :castShadow="true"

        />

 <StarsParticles
    :key="`${isLowPower ? 'low' : 'high'}-${isReducedVisuals ? 'reduced' : 'full'}`"
    :stars-count="starFieldCount"
    :size="starFieldSize"
    color="#77ccff"
    :speed="starFieldSpeed"
    :opacity="starFieldOpacity"
    :sizes="starFieldVolume"
    :disabled="isReducedVisuals"
  />

  <IlluminatedCircle v-if="footerCircle && isFooterCircleActive"
      ref="circleRef"
      :linkedObject="'.pre-footer'"
        :position="footerCircle.position"
        :scale="footerCircle.scale"
        :disabled="isReducedVisuals"
  />


  <template v-if="pageContent">
      
      <template v-for="(content, key) in pageContent" :key="key">
        <Suspense>
          <component
            v-if="threeComponents[content.component]"
            :is="threeComponents[content.component]"
            :class="content.component.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase() + '-' + key "
            :linkedObject="`.${content.component.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase() + '-' + key }`"
            v-bind="content.props"
          /> 
        </Suspense>
       
      </template>
    </template>
  </TresCanvas>
</template>

<style lang="scss" scoped>
#main-canvas {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100dvh;
  pointer-events: none;
  z-index: -10;
  background-color: $background-color;
}
</style>
